import React, { useState, useEffect } from 'react';
import { getEmailSettings, updateEmailSettings } from '../api/lenderApi';

const GeneralEmailSettings = React.forwardRef((props, ref) => {
  const [settings, setSettings] = useState({
    mandatoryCcEnabled: false,
    juniorAeField: 'Junior_AE__c',
    seniorAeField: 'Account_Executive__c'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadEmailSettings();
  }, []);

  const loadEmailSettings = async () => {
    try {
      setLoading(true);
      const emailSettings = await getEmailSettings();
      setSettings({
        mandatoryCcEnabled: emailSettings.mandatoryCcEnabled || false,
        juniorAeField: emailSettings.juniorAeField || 'Junior_AE__c',
        seniorAeField: emailSettings.seniorAeField || 'Account_Executive__c'
      });
      setError('');
    } catch (err) {
      console.error('Error loading email settings:', err);
      setError('Failed to load email settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      
      await updateEmailSettings(settings);
      
      setSuccessMessage('Email settings saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      return true;
    } catch (err) {
      console.error('Error saving email settings:', err);
      setError('Failed to save email settings. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Expose save function to parent via ref
  React.useImperativeHandle(ref, () => ({
    save: handleSave,
    saving: saving
  }));

  const updateSetting = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="general-email-settings">
        <div className="loading-spinner">Loading email settings...</div>
      </div>
    );
  }

  return (
    <div className="general-email-settings">
      <div className="settings-section">
        <h3>General Email Settings</h3>
        <p className="subtitle">Configure global email behavior for all email lenders</p>
        
        {/* Error/Success Messages */}
        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}

        <div className="mandatory-cc-section">
          <h4>Mandatory CC Configuration</h4>
          
          <div className="setting-item">
            <label>ENABLE MANDATORY CC</label>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.mandatoryCcEnabled}
                onChange={(e) => updateSetting('mandatoryCcEnabled', e.target.checked)}
              />
              <span className="slider"></span>
            </label>
            <div className="setting-description">
              When enabled, all email lenders will automatically CC the Junior and Senior Account Executives from the opportunity record.
            </div>
          </div>

          {settings.mandatoryCcEnabled && (
            <>
              <div className="ae-fields-grid">
                <div className="ae-field-column">
                  <div className="setting-item">
                    <label>JUNIOR ACCOUNT EXECUTIVE FIELD</label>
                    <input
                      type="text"
                      value={settings.juniorAeField}
                      onChange={(e) => updateSetting('juniorAeField', e.target.value)}
                      placeholder="Junior_AE__c"
                      disabled
                    />
                    <div className="setting-description">
                      Salesforce field name for the Junior Account Executive user lookup on the Opportunity object.
                    </div>
                  </div>
                </div>

                <div className="ae-field-column">
                  <div className="setting-item">
                    <label>SENIOR ACCOUNT EXECUTIVE FIELD</label>
                    <input
                      type="text"
                      value={settings.seniorAeField}
                      onChange={(e) => updateSetting('seniorAeField', e.target.value)}
                      placeholder="Account_Executive__c"
                      disabled
                    />
                    <div className="setting-description">
                      Salesforce field name for the Senior Account Executive user lookup on the Opportunity object.
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-box">
                <strong>How it works:</strong>
                <ul>
                  <li>When an email lender submission is made, the system will look up the Junior and Senior AE users from the opportunity</li>
                  <li>Their email addresses will be automatically added to the CC field of all outgoing lender emails</li>
                  <li>This applies to all email-only lenders regardless of their individual CC settings</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default GeneralEmailSettings;